import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ViralClipOverlay from "./ViralClipOverlay";
import socialClipApi from "../../api/socialClipApi";
import "./ViralClipComposer.css";

/**
 * Coach-facing composer: pick a rally, preview the exact composite, render it
 * server-side, then share or publish.
 *
 * The preview loops the chosen window of the original video behind a canvas
 * drawing the server's layout. It is deliberately not a second renderer — the
 * server is the only thing that produces a real file.
 */

const PLATFORM_LABELS = {
  instagram: "Instagram",
  facebook: "Facebook Page",
  tiktok: "TikTok",
};

function formatSeconds(ms) {
  const s = Math.max(0, Number(ms) / 1000);
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
}

export default function ViralClipComposer({
  teId,
  videoUrl,
  userId = null,
  clubId = null,
  onClose = null,
}) {
  const videoRef = useRef(null);
  const abortRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [clip, setClip] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [progress, setProgress] = useState(0);

  const [caption, setCaption] = useState("");
  const [captionBusy, setCaptionBusy] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const candidates = useMemo(() => {
    if (!data) return [];
    const list = data.candidates?.length ? data.candidates : [data.best].filter(Boolean);
    return list;
  }, [data]);

  const selected = candidates[selectedIndex] || null;
  const consent = data?.consent || null;
  const canRender = !!selected && consent?.allowed && !rendering;

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await socialClipApi.getClipCandidates({
          teId,
          userId,
          signal: controller.signal,
        });
        setData(result);
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message);
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [teId, userId]);

  useEffect(() => {
    let cancelled = false;
    socialClipApi
      .listSocialAccounts({ clubId })
      .then((result) => {
        if (!cancelled) setAccounts(result?.accounts || []);
      })
      .catch(() => {
        // No connected accounts is a normal state, not an error to surface.
      });
    return () => {
      cancelled = true;
    };
  }, [clubId]);

  // Loop the selected rally window so the coach sees what will be cut.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !selected) return undefined;

    const startSec = Number(selected.start_ms) / 1000;
    const endSec = Number(selected.end_ms) / 1000;

    const onTimeUpdate = () => {
      if (video.currentTime >= endSec || video.currentTime < startSec - 0.5) {
        video.currentTime = startSec;
      }
    };

    video.currentTime = startSec;
    video.addEventListener("timeupdate", onTimeUpdate);
    video.play().catch(() => {});

    return () => video.removeEventListener("timeupdate", onTimeUpdate);
  }, [selected]);

  const handleRender = useCallback(async () => {
    if (!selected) return;
    setRendering(true);
    setProgress(0);
    setError(null);

    try {
      const { clip: created } = await socialClipApi.startClipRender({
        te_id: teId,
        start_ms: selected.start_ms,
        end_ms: selected.end_ms,
        stroke_count: selected.stroke_count,
        user_id: userId,
        table_id: selected.table_id,
        geometry_id: selected.geometry_id,
      });

      setClip(created);

      const ready = await socialClipApi.pollClipUntilReady(created.clip_id, {
        onProgress: (c) => setProgress(Number(c.progress) || 0),
      });

      setClip(ready);
      setCaption(ready.caption || "");

      if (!ready.caption) {
        setCaptionBusy(true);
        try {
          const { caption: generated } = await socialClipApi.generateCaption(
            ready.clip_id
          );
          setCaption(generated);
        } finally {
          setCaptionBusy(false);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setRendering(false);
    }
  }, [selected, teId, userId]);

  const handleRegenerateCaption = useCallback(async () => {
    if (!clip) return;
    setCaptionBusy(true);
    try {
      const { caption: generated } = await socialClipApi.generateCaption(
        clip.clip_id,
        { regenerate: true }
      );
      setCaption(generated);
    } catch (err) {
      setError(err.message);
    } finally {
      setCaptionBusy(false);
    }
  }, [clip]);

  const handleCopyLink = useCallback(async () => {
    if (!clip?.share_token) return;
    await navigator.clipboard.writeText(socialClipApi.clipShareUrl(clip.share_token));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [clip]);

  const handlePublish = useCallback(async () => {
    if (!clip || !selectedAccounts.length) return;
    setPublishing(true);
    setPublishResult(null);
    try {
      await socialClipApi.updateClip(clip.clip_id, { caption });
      const result = await socialClipApi.publishClip({
        clip_id: clip.clip_id,
        account_ids: selectedAccounts,
        caption,
      });
      setPublishResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setPublishing(false);
    }
  }, [clip, selectedAccounts, caption]);

  const toggleAccount = (accountId) => {
    setSelectedAccounts((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    );
  };

  if (loading) {
    return (
      <div className="vcc">
        <div className="vcc__empty">Looking for the best rally…</div>
      </div>
    );
  }

  if (!candidates.length) {
    return (
      <div className="vcc">
        <div className="vcc__empty">
          <p>No rally long enough to make a clip from.</p>
          <span>
            A clip needs a continuous exchange. Run the pose analysis on this
            exercise first, or pick one with a longer rally.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="vcc">
      <header className="vcc__header">
        <div>
          <h3>Share this rally</h3>
          <p>Rendered at broadcast quality, ready for Reels, TikTok and Stories.</p>
        </div>
        {onClose && (
          <button type="button" className="vcc__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        )}
      </header>

      {consent && !consent.allowed && (
        <div className="vcc__consent vcc__consent--blocked">
          <strong>Public sharing is blocked.</strong>
          <span>
            {consent.reason === "no_subjects_resolved"
              ? "We cannot confirm who appears in this footage."
              : `Missing public-use consent for ${consent.missing
                  .map((m) => m.display_name || m.user_id)
                  .join(", ")}.`}
          </span>
        </div>
      )}
      {consent?.allowed && (
        <div className="vcc__consent vcc__consent--ok">
          <strong>Consent on file</strong>
          <span>All players in this clip have public-use consent.</span>
        </div>
      )}

      <div className="vcc__body">
        <div className="vcc__stage">
          <div className="vcc__phone">
            {clip?.status === "ready" || clip?.status === "published" ? (
              <video
                key={clip.video_url}
                className="vcc__video"
                src={clip.video_url}
                poster={clip.thumbnail_url || undefined}
                controls
                playsInline
                loop
              />
            ) : (
              <>
                <video
                  ref={videoRef}
                  className="vcc__video"
                  src={videoUrl}
                  muted
                  playsInline
                  loop
                  preload="auto"
                />
                {data?.layout && <ViralClipOverlay layout={data.layout} />}
              </>
            )}

            {rendering && (
              <div className="vcc__rendering">
                <div className="vcc__spinner" />
                <span>Rendering… {Math.round(progress * 100)}%</span>
              </div>
            )}
          </div>
        </div>

        <div className="vcc__panel">
          <section>
            <h4>Rally</h4>
            <div className="vcc__candidates">
              {candidates.map((c, i) => (
                <button
                  type="button"
                  key={`${c.start_ms}-${c.heuristic_id}`}
                  className={`vcc__candidate ${i === selectedIndex ? "is-selected" : ""}`}
                  onClick={() => setSelectedIndex(i)}
                  disabled={rendering || !!clip}
                >
                  <strong>{c.stroke_count} strokes</strong>
                  <span>
                    {formatSeconds(c.start_ms)} – {formatSeconds(c.end_ms)}
                  </span>
                </button>
              ))}
            </div>
          </section>

          {!clip && (
            <button
              type="button"
              className="vcc__primary"
              onClick={handleRender}
              disabled={!canRender}
            >
              {rendering ? "Rendering…" : "Create shareable clip"}
            </button>
          )}

          {clip?.status === "ready" || clip?.status === "published" ? (
            <>
              <section>
                <h4>
                  Caption
                  <button
                    type="button"
                    className="vcc__link"
                    onClick={handleRegenerateCaption}
                    disabled={captionBusy}
                  >
                    {captionBusy ? "Writing…" : "Regenerate"}
                  </button>
                </h4>
                <textarea
                  className="vcc__caption"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  rows={7}
                  maxLength={2200}
                  placeholder="Write a caption…"
                />
              </section>

              <section>
                <h4>Share</h4>
                <div className="vcc__actions">
                  <a
                    className="vcc__secondary"
                    href={clip.video_url}
                    download
                    target="_blank"
                    rel="noreferrer"
                  >
                    Download MP4
                  </a>
                  <button type="button" className="vcc__secondary" onClick={handleCopyLink}>
                    {copied ? "Link copied" : "Copy share link"}
                  </button>
                </div>
                <p className="vcc__hint">
                  Download to post from a personal account — Instagram does not
                  allow apps to publish to personal profiles.
                </p>
              </section>

              {accounts.length > 0 && (
                <section>
                  <h4>Publish from the club</h4>
                  <div className="vcc__accounts">
                    {accounts.map((account) => (
                      <label key={account.account_id} className="vcc__account">
                        <input
                          type="checkbox"
                          checked={selectedAccounts.includes(account.account_id)}
                          onChange={() => toggleAccount(account.account_id)}
                          disabled={publishing || account.is_expired}
                        />
                        {account.external_account_avatar && (
                          <img src={account.external_account_avatar} alt="" />
                        )}
                        <span>
                          <strong>
                            {account.external_account_name || account.external_account_id}
                          </strong>
                          <em>{PLATFORM_LABELS[account.platform] || account.platform}</em>
                        </span>
                        {account.is_expired && <b className="vcc__badge">Reconnect</b>}
                      </label>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="vcc__primary"
                    onClick={handlePublish}
                    disabled={publishing || !selectedAccounts.length}
                  >
                    {publishing ? "Publishing…" : `Publish to ${selectedAccounts.length}`}
                  </button>
                </section>
              )}

              {publishResult && (
                <div className="vcc__result">
                  {publishResult.queued?.length > 0 && (
                    <p>
                      Queued for {publishResult.queued.map((q) => q.platform).join(", ")}.
                      Publishing takes a minute or two.
                    </p>
                  )}
                  {publishResult.rejected?.map((r) => (
                    <p key={r.account_id} className="vcc__error">
                      {r.error}
                    </p>
                  ))}
                </div>
              )}
            </>
          ) : null}

          {error && <div className="vcc__error">{error}</div>}
        </div>
      </div>
    </div>
  );
}
